<div *ngIf="loading" class="dash-loading">{{ 'app.Loading' | translate }}…</div>

<div *ngIf="!loading && loadFailed" class="dash-card dash-failed">
  <i class="bi bi-exclamation-circle"></i>
  <p>{{ 'dashHub.LoadFailed' | translate }}</p>
  <button type="button" class="btn btn-sm btn-outline-secondary" (click)="load()">{{ 'dashHub.Retry' | translate }}</button>
</div>

<div class="dash" *ngIf="!loading && data">

  <!-- ─────────────── STAFF: school-wide overview ─────────────── -->
  <ng-container *ngIf="isStaff">

    <!-- What needs attention today -->
    <section class="dash-actions" *ngIf="data.actionItems as a" [attr.aria-label]="'dashHub.NeedsAttention' | translate">
      <div class="dash-actions__head">
        <span class="dash-actions__title"><i class="bi bi-lightning-charge"></i>{{ 'dashHub.NeedsAttention' | translate }}</span>
        <span class="dash-actions__ok" *ngIf="actionTotal === 0"><i class="bi bi-check2-circle"></i>{{ 'dashHub.AllClear' | translate }}</span>
      </div>
      <div class="dash-actions__items">
        <a class="dash-action" routerLink="/settings/user-approval" [class.is-hot]="a.pendingUserApprovals > 0">
          <span class="dash-action__icon dash-action__icon--violet"><i class="bi bi-person-check"></i></span>
          <span class="dash-action__value">{{ a.pendingUserApprovals | number }}</span>
          <span class="dash-action__label">{{ 'dashHub.PendingApprovals' | translate }}</span>
        </a>
        <a class="dash-action" routerLink="/assessments/grading" [class.is-hot]="a.ungradedSubmissions > 0">
          <span class="dash-action__icon dash-action__icon--amber"><i class="bi bi-pencil-square"></i></span>
          <span class="dash-action__value">{{ a.ungradedSubmissions | number }}</span>
          <span class="dash-action__label">{{ 'dashHub.UngradedSubmissions' | translate }}</span>
        </a>
        <a class="dash-action" *ngIf="hasTutoring" routerLink="/academy/bookings" [class.is-hot]="a.pendingBookings > 0">
          <span class="dash-action__icon dash-action__icon--blue"><i class="bi bi-calendar-plus"></i></span>
          <span class="dash-action__value">{{ a.pendingBookings | number }}</span>
          <span class="dash-action__label">{{ 'dashHub.PendingBookings' | translate }}</span>
        </a>
        <a class="dash-action" *ngIf="hasTutoring" routerLink="/lessons/schedule">
          <span class="dash-action__icon dash-action__icon--teal"><i class="bi bi-person-video3"></i></span>
          <span class="dash-action__value">{{ a.lessonsToday | number }}</span>
          <span class="dash-action__label">{{ 'dashHub.LessonsToday' | translate }}</span>
        </a>
      </div>
    </section>

    <div class="dash-kpis dash-kpis--4">
      <div class="dash-kpi">
        <div class="dash-kpi__head">
          <span class="dash-kpi__label">{{ 'dashboard.Students' | translate }}</span>
          <span class="dash-kpi__icon dash-kpi__icon--blue"><i class="icon ico-students"></i></span>
        </div>
        <div class="dash-kpi__value">{{ data.studentCount | number }}</div>
        <div class="dash-kpi__foot">
          <span class="dash-delta" *ngIf="studentsDelta !== null"
            [class.dash-delta--up]="studentsDelta >= 0" [class.dash-delta--down]="studentsDelta < 0">
            <i class="bi" [class.bi-arrow-up-right]="studentsDelta >= 0" [class.bi-arrow-down-right]="studentsDelta < 0"></i>
            <span dir="ltr">{{ studentsDelta >= 0 ? '+' : '' }}{{ studentsDelta }}%</span>
          </span>
          <span class="dash-kpi__caption">{{ 'dashboard.EnrolledLearners' | translate }}</span>
        </div>
      </div>

      <div class="dash-kpi">
        <div class="dash-kpi__head">
          <span class="dash-kpi__label">{{ 'dashboard.Teachers' | translate }}</span>
          <span class="dash-kpi__icon dash-kpi__icon--violet"><i class="icon ico-students"></i></span>
        </div>
        <div class="dash-kpi__value">{{ data.teacherCount | number }}</div>
        <div class="dash-kpi__foot"><span class="dash-kpi__caption">{{ 'dashboard.TeachingStaff' | translate }}</span></div>
      </div>

      <div class="dash-kpi">
        <div class="dash-kpi__head">
          <span class="dash-kpi__label">{{ 'dashboard.Attendance' | translate }}</span>
          <span class="dash-kpi__icon dash-kpi__icon--amber"><i class="icon ico-files-check"></i></span>
        </div>
        <div class="dash-kpi__value">{{ ratePct(data.attendanceRate) }}%</div>
        <div class="dash-kpi__foot"><span class="dash-kpi__caption">{{ 'dashboard.OverallRate' | translate }}</span></div>
      </div>

      <div class="dash-kpi">
        <div class="dash-kpi__head">
          <span class="dash-kpi__label">{{ 'dashboard.ExamAverage' | translate }}</span>
          <span class="dash-kpi__icon dash-kpi__icon--teal"><i class="icon ico-quizzes"></i></span>
        </div>
        <div class="dash-kpi__value">{{ round(data.examAverage) }}%</div>
        <div class="dash-kpi__foot"><span class="dash-kpi__caption">{{ 'dashboard.AcrossAllExams' | translate }}</span></div>
      </div>
    </div>

    <!-- LMS: tenant-wide course overview (tenant admins only; hides itself when unavailable) -->
    <app-lms-overview-card *ngIf="isTenantAdmin"></app-lms-overview-card>

    <ng-container *ngTemplateOutlet="charts"></ng-container>

    <div class="dash-grid">
      <div class="dash-col">
        <ng-container *ngTemplateOutlet="lecturesCard"></ng-container>
        <ng-container *ngTemplateOutlet="rankings"></ng-container>
      </div>

      <div class="dash-col">
        <div class="dash-card">
          <div class="dash-card__title">{{ 'dashboard.AtAGlance' | translate }}</div>
          <div class="dash-stat"><span>{{ 'dashboard.Students' | translate }}</span><span>{{ data.studentCount | number }}</span></div>
          <div class="dash-stat"><span>{{ 'dashboard.Teachers' | translate }}</span><span>{{ data.teacherCount | number }}</span></div>
          <div class="dash-stat"><span>{{ 'dashboard.OverallAttendance' | translate }}</span><span>{{ ratePct(data.attendanceRate) }}%</span></div>
          <div class="dash-stat"><span>{{ 'dashboard.ExamAverage' | translate }}</span><span>{{ round(data.examAverage) }}%</span></div>
        </div>
      </div>
    </div>
  </ng-container>

  <!-- ─────────────── TEACHER: my students, my classes, my queue ─────────────── -->
  <ng-container *ngIf="isTeacher && data.teacher as t">
    <div class="dash-kpis dash-kpis--4">
      <div class="dash-kpi">
        <div class="dash-kpi__head">
          <span class="dash-kpi__label">{{ 'dashHub.MyStudents' | translate }}</span>
          <span class="dash-kpi__icon dash-kpi__icon--blue"><i class="bi bi-people"></i></span>
        </div>
        <div class="dash-kpi__value">{{ t.myStudents | number }}</div>
        <div class="dash-kpi__foot">
          <span class="dash-kpi__caption">{{ 'dashHub.StudentsSplit' | translate: { grade: t.gradeStudents, private: t.privateStudents } }}</span>
        </div>
      </div>

      <a class="dash-kpi dash-kpi--link" routerLink="/assessments/grading">
        <div class="dash-kpi__head">
          <span class="dash-kpi__label">{{ 'dashHub.GradingQueue' | translate }}</span>
          <span class="dash-kpi__icon dash-kpi__icon--amber"><i class="bi bi-pencil-square"></i></span>
        </div>
        <div class="dash-kpi__value">{{ t.gradingQueue | number }}</div>
        <div class="dash-kpi__foot"><span class="dash-kpi__caption">{{ 'dashHub.AwaitingYourMarking' | translate }}</span></div>
      </a>

      <a class="dash-kpi dash-kpi--link" *ngIf="hasTutoring" routerLink="/lessons/schedule">
        <div class="dash-kpi__head">
          <span class="dash-kpi__label">{{ 'dashHub.LessonsToday' | translate }}</span>
          <span class="dash-kpi__icon dash-kpi__icon--teal"><i class="bi bi-person-video3"></i></span>
        </div>
        <div class="dash-kpi__value">{{ t.lessonsToday | number }}</div>
        <div class="dash-kpi__foot">
          <span class="dash-kpi__caption" [class.dash-kpi__caption--warn]="t.reportsDue > 0">
            {{ 'dashHub.ReportsDue' | translate: { count: t.reportsDue } }}
          </span>
        </div>
      </a>

      <a class="dash-kpi dash-kpi--link" *ngIf="hasTutoring" routerLink="/academy/bookings">
        <div class="dash-kpi__head">
          <span class="dash-kpi__label">{{ 'dashHub.BookingRequests' | translate }}</span>
          <span class="dash-kpi__icon dash-kpi__icon--violet"><i class="bi bi-calendar-plus"></i></span>
        </div>
        <div class="dash-kpi__value">{{ t.pendingBookings | number }}</div>
        <div class="dash-kpi__foot"><span class="dash-kpi__caption">{{ 'dashHub.WaitingForYou' | translate }}</span></div>
      </a>

      <div class="dash-kpi" *ngIf="!hasTutoring">
        <div class="dash-kpi__head">
          <span class="dash-kpi__label">{{ 'dashboard.Attendance' | translate }}</span>
          <span class="dash-kpi__icon dash-kpi__icon--teal"><i class="icon ico-files-check"></i></span>
        </div>
        <div class="dash-kpi__value">{{ ratePct(data.attendanceRate) }}%</div>
        <div class="dash-kpi__foot"><span class="dash-kpi__caption">{{ 'dashHub.Last30Days' | translate }}</span></div>
      </div>

      <div class="dash-kpi" *ngIf="!hasTutoring">
        <div class="dash-kpi__head">
          <span class="dash-kpi__label">{{ 'dashboard.ExamAverage' | translate }}</span>
          <span class="dash-kpi__icon dash-kpi__icon--violet"><i class="icon ico-quizzes"></i></span>
        </div>
        <div class="dash-kpi__value">{{ round(data.examAverage) }}%</div>
        <div class="dash-kpi__foot"><span class="dash-kpi__caption">{{ 'dashHub.MyExams' | translate }}</span></div>
      </div>
    </div>

    <div class="dash-grid">
      <div class="dash-col">
        <div class="dash-card" *ngIf="hasTutoring">
          <div class="dash-card__head">
            <div class="dash-card__title">{{ 'dashHub.MyUpcomingLessons' | translate }}</div>
            <a routerLink="/lessons/schedule" class="dash-link">{{ 'dashHub.SeeSchedule' | translate }}</a>
          </div>
          <ng-container *ngTemplateOutlet="lessonList; context: { lessons: data.upcomingLessons }"></ng-container>
        </div>
        <ng-container *ngTemplateOutlet="lecturesCard"></ng-container>
        <ng-container *ngTemplateOutlet="rankings"></ng-container>
      </div>

      <div class="dash-col">
        <div class="dash-card">
          <div class="dash-card__title">{{ 'dashboard.AtAGlance' | translate }}</div>
          <div class="dash-stat"><span>{{ 'dashHub.MyGrades' | translate }}</span><span>{{ t.myGrades | number }}</span></div>
          <div class="dash-stat"><span>{{ 'dashHub.GradeStudents' | translate }}</span><span>{{ t.gradeStudents | number }}</span></div>
          <div class="dash-stat" *ngIf="hasTutoring"><span>{{ 'dashHub.PrivateStudents' | translate }}</span><span>{{ t.privateStudents | number }}</span></div>
          <div class="dash-stat"><span>{{ 'dashHub.AttendanceLast30' | translate }}</span><span>{{ ratePct(data.attendanceRate) }}%</span></div>
          <div class="dash-stat"><span>{{ 'dashboard.ExamAverage' | translate }}</span><span>{{ round(data.examAverage) }}%</span></div>
        </div>

        <div class="dash-card" *ngIf="data.gradeDistribution.length">
          <div class="dash-card__title">{{ 'dashHub.MyStudentsByGrade' | translate }}</div>
          <div echarts [options]="gradeOption" class="dash-chart"></div>
        </div>
      </div>
    </div>
  </ng-container>

  <!-- ─────────────── PARENT: one card per child ─────────────── -->
  <ng-container *ngIf="isParent">
    <div class="dash-head">
      <h1>{{ 'dashHub.MyChildren' | translate }}</h1>
      <p>{{ 'dashHub.MyChildrenHint' | translate }}</p>
    </div>

    <div *ngIf="!data.children.length" class="dash-card dash-failed">
      <i class="bi bi-people"></i>
      <p>{{ 'dashHub.NoChildren' | translate }}</p>
      <span class="dash-muted">{{ 'parent.NoChildren' | translate }}</span>
    </div>

    <div class="dash-children" *ngIf="data.children.length">
      <a class="dash-child" *ngFor="let c of data.children; trackBy: trackChild" [routerLink]="['/parent']" [queryParams]="{ child: c.childId }">
        <div class="dash-child__head">
          <span class="dash-child__avatar" aria-hidden="true">{{ childName(c).charAt(0) }}</span>
          <span class="dash-child__who">
            <span class="dash-child__name" dir="auto">{{ childName(c) }}</span>
            <span class="dash-child__grade" dir="auto">{{ childGrade(c) || ('parent.NoGrade' | translate) }}</span>
          </span>
          <i class="bi bi-chevron-right dash-flip" aria-hidden="true"></i>
        </div>

        <div class="dash-child__stats">
          <div>
            <span class="dash-child__value" dir="ltr">{{ c.attendanceRate30d != null ? ratePct(c.attendanceRate30d) + '%' : '—' }}</span>
            <span class="dash-child__label">{{ 'dashHub.Attendance30' | translate }}</span>
          </div>
          <div>
            <span class="dash-child__value" dir="ltr">{{ c.recentAverageScore != null ? round(c.recentAverageScore) + '%' : '—' }}</span>
            <span class="dash-child__label">{{ 'dashHub.RecentAverage' | translate }}</span>
          </div>
          <div>
            <span class="dash-child__value" [class.dash-child__value--warn]="c.pendingAssignments > 0">{{ c.pendingAssignments }}</span>
            <span class="dash-child__label">{{ 'dashHub.PendingAssignments' | translate }}</span>
          </div>
        </div>

        <div class="dash-child__foot" *ngIf="c.nextLesson || c.lastLessonReportAt">
          <span *ngIf="c.nextLesson as l">
            <i class="bi bi-calendar-event"></i>
            {{ 'dashHub.NextLessonAt' | translate: { when: (l.startUtc | localizedDate: 'dayTime') } }}
            <ng-container *ngIf="lessonTeacher(c)"> · <span dir="auto">{{ lessonTeacher(c) }}</span></ng-container>
          </span>
          <span *ngIf="c.lastLessonReportAt">
            <i class="bi bi-journal-text"></i>
            {{ 'dashHub.LastReportOn' | translate: { when: (c.lastLessonReportAt | localizedDate) } }}
          </span>
        </div>
      </a>
    </div>
  </ng-container>

  <!-- ─────────────── STUDENT: personal dashboard ─────────────── -->
  <ng-container *ngIf="isStudent">
    <div class="dash-kpis">
      <div class="dash-kpi">
        <div class="dash-kpi__head">
          <span class="dash-kpi__label">{{ 'dashboard.MyAttendance' | translate }}</span>
          <span class="dash-kpi__icon dash-kpi__icon--blue"><i class="icon ico-students"></i></span>
        </div>
        <div class="dash-kpi__value">{{ ratePct(data.attendanceRate) }}%</div>
        <div class="dash-kpi__foot"><span class="dash-kpi__caption">{{ 'dashboard.ThisTerm' | translate }}</span></div>
      </div>
      <div class="dash-kpi">
        <div class="dash-kpi__head">
          <span class="dash-kpi__label">{{ 'dashboard.ExamsTaken' | translate }}</span>
          <span class="dash-kpi__icon dash-kpi__icon--amber"><i class="icon ico-files-check"></i></span>
        </div>
        <div class="dash-kpi__value">{{ data.examsTaken }}</div>
        <div class="dash-kpi__foot"><span class="dash-kpi__caption">{{ 'dashboard.Completed' | translate }}</span></div>
      </div>
      <div class="dash-kpi">
        <div class="dash-kpi__head">
          <span class="dash-kpi__label">{{ 'dashboard.ExamAverage' | translate }}</span>
          <span class="dash-kpi__icon dash-kpi__icon--teal"><i class="icon ico-quizzes"></i></span>
        </div>
        <div class="dash-kpi__value">{{ round(data.examAverage) }}%</div>
        <div class="dash-kpi__foot"><span class="dash-kpi__caption">{{ 'dashboard.AcrossAllExams' | translate }}</span></div>
      </div>
    </div>

    <!-- LMS: resume enrolled courses (students only; the API is Student-role) -->
    <app-continue-learning-card></app-continue-learning-card>

    <div class="dash-grid">
      <div class="dash-col">
        <div class="dash-card">
          <div class="dash-card__head">
            <div class="dash-card__title">{{ 'dashHub.DueSoon' | translate }}</div>
            <span class="dash-badge" *ngIf="data.pendingAssignments > 0">{{ 'dashHub.PendingCount' | translate: { count: data.pendingAssignments } }}</span>
          </div>
          <p class="dash-empty" *ngIf="!data.dueSoon.length">{{ 'dashHub.NothingDue' | translate }}</p>
          <a class="dash-due" *ngFor="let d of data.dueSoon"
             [routerLink]="d.kind === 'exam' ? '/student/exams' : '/student/assignments'"
             [queryParams]="d.kind === 'exam' ? { exam: d.id } : { assignment: d.id }">
            <span class="dash-due__icon" [class.dash-due__icon--exam]="d.kind === 'exam'">
              <i class="bi" [class.bi-journal-check]="d.kind === 'exam'" [class.bi-clipboard-check]="d.kind !== 'exam'"></i>
            </span>
            <span class="dash-due__body">
              <span class="dash-due__title" dir="auto">{{ d.title }}</span>
              <span class="dash-due__kind">{{ (d.kind === 'exam' ? 'dashHub.Exam' : 'dashHub.Assignment') | translate }}</span>
            </span>
            <span class="dash-due__when">{{ d.dueAt | localizedDate: 'dayTime' }}</span>
          </a>
        </div>

        <ng-container *ngTemplateOutlet="lecturesCard"></ng-container>

        <div class="dash-card" *ngIf="hasTutoring && data.upcomingLessons.length">
          <div class="dash-card__head">
            <div class="dash-card__title">{{ 'dashHub.MyUpcomingLessons' | translate }}</div>
            <a routerLink="/lessons/my" class="dash-link">{{ 'dashHub.SeeAll' | translate }}</a>
          </div>
          <ng-container *ngTemplateOutlet="lessonList; context: { lessons: data.upcomingLessons }"></ng-container>
        </div>
      </div>

      <div class="dash-col">
        <div class="dash-card">
          <div class="dash-card__title">{{ 'dashboard.MyPerformance' | translate }}</div>
          <div class="dash-bar">
            <div class="dash-bar__row"><span>{{ 'dashboard.Attendance' | translate }}</span><span>{{ ratePct(data.attendanceRate) }}%</span></div>
            <div class="dash-bar__track"><span [style.width.%]="ratePct(data.attendanceRate)"></span></div>
          </div>
          <div class="dash-bar">
            <div class="dash-bar__row"><span>{{ 'dashboard.ExamAverage' | translate }}</span><span>{{ round(data.examAverage) }}%</span></div>
            <div class="dash-bar__track"><span [style.width.%]="data.examAverage"></span></div>
          </div>
          <div class="dash-bar">
            <div class="dash-bar__row"><span>{{ 'dashboard.PassRate' | translate }}</span><span>{{ ratePct(data.passRate) }}%</span></div>
            <div class="dash-bar__track"><span [style.width.%]="ratePct(data.passRate)"></span></div>
          </div>
          <div class="dash-bar__row dash-bar__row--plain"><span>{{ 'dashboard.AssignmentsSubmitted' | translate }}</span><span>{{ data.assignmentsSubmitted }}</span></div>
        </div>
      </div>
    </div>
  </ng-container>
</div>

<!-- ─────────────── shared blocks ─────────────── -->

<ng-template #charts>
  <div class="dash-charts" *ngIf="data">
    <div class="dash-card">
      <div class="dash-card__title">{{ 'dashboard.StudentEnrollment' | translate }}</div>
      <div class="dash-card__sub">{{ 'dashboard.CumulativeLastMonths' | translate }}</div>
      <div echarts [options]="enrollmentOption" class="dash-chart"></div>
    </div>
    <div class="dash-card">
      <div class="dash-card__title">{{ 'dashboard.StudentsByGrade' | translate }}</div>
      <div class="dash-card__sub">{{ data.studentCount | number }} {{ 'dashboard.Students' | translate }}</div>
      <div echarts [options]="gradeOption" class="dash-chart" *ngIf="data.gradeDistribution.length; else noGrades"></div>
      <ng-template #noGrades><p class="dash-empty dash-empty--pad">{{ 'dashboard.NoGradesYet' | translate }}</p></ng-template>
    </div>
  </div>
</ng-template>

<ng-template #lecturesCard>
  <div class="dash-card">
    <div class="dash-card__title">{{ 'dashboard.UpcomingLectures' | translate }}</div>
    <div *ngIf="activeDays.length; else noLectures">
      <div class="dash-days">
        <button *ngFor="let d of activeDays" type="button" class="dash-days__tab"
          [class.is-active]="d === activeDay" (click)="activeDay = d">{{ dayLabel(d) }}</button>
      </div>
      <div class="dash-lectures">
        <div class="dash-lecture" *ngFor="let lec of lecturesForDay(activeDay)">
          <div class="dash-lecture__time" dir="ltr">
            <span>{{ lec.time.split(' - ')[0] }}</span>
            <span>{{ lec.time.split(' - ')[1] }}</span>
          </div>
          <div class="dash-lecture__body">
            <h6 dir="auto">{{ lec.title }}</h6>
            <span class="dash-lecture__grade" *ngIf="lec.gradeName">{{ localized(lec.gradeNameEn, lec.gradeNameAr, lec.gradeName) }}</span>
          </div>
          <div class="dash-lecture__status">
            <span class="dash-pill dash-pill--live" *ngIf="lec.isLive">{{ 'dashboard.Live' | translate }}</span>
            <i class="icon ico-videocam"></i>
          </div>
        </div>
      </div>
    </div>
    <ng-template #noLectures><p class="dash-empty">{{ 'dashboard.NoUpcomingLectures' | translate }}</p></ng-template>
  </div>
</ng-template>

<ng-template #rankings>
  <div class="dash-two" *ngIf="data">
    <div class="dash-card">
      <div class="dash-card__title">{{ 'dashboard.Top5Attendance' | translate }}</div>
      <p class="dash-empty" *ngIf="!data.topByAttendance.length">{{ 'dashboard.NoDataYet' | translate }}</p>
      <div class="dash-rank" *ngFor="let s of data.topByAttendance; let i = index">
        <span class="dash-rank__no">{{ i + 1 }}</span>
        <span class="dash-rank__name" dir="auto">{{ localized(s.nameEn, s.nameAr, s.name) }}</span>
        <span class="dash-rank__val">{{ round(s.value) }}%</span>
      </div>
    </div>
    <div class="dash-card">
      <div class="dash-card__title">{{ 'dashboard.Top5Exam' | translate }}</div>
      <p class="dash-empty" *ngIf="!data.topByExam.length">{{ 'dashboard.NoDataYet' | translate }}</p>
      <div class="dash-rank" *ngFor="let s of data.topByExam; let i = index">
        <span class="dash-rank__no">{{ i + 1 }}</span>
        <span class="dash-rank__name" dir="auto">{{ localized(s.nameEn, s.nameAr, s.name) }}</span>
        <span class="dash-rank__val">{{ round(s.value) }}%</span>
      </div>
    </div>
  </div>
</ng-template>

<ng-template #lessonList let-lessons="lessons">
  <p class="dash-empty" *ngIf="!lessons.length">{{ 'dashHub.NoUpcomingLessons' | translate }}</p>
  <div class="dash-lectures" *ngIf="lessons.length">
    <div class="dash-lecture" *ngFor="let l of lessons; trackBy: trackLesson">
      <div class="dash-lecture__time">
        <span>{{ l.startUtc | localizedDate: 'time' }}</span>
        <span>{{ l.startUtc | localizedDate: 'short' }}</span>
      </div>
      <div class="dash-lecture__body">
        <h6 dir="auto">{{ l.subject || ('dashHub.Lesson' | translate) }}</h6>
        <span class="dash-lecture__grade" dir="auto">{{ lessonWith(l) }}</span>
      </div>
      <div class="dash-lecture__status">
        <span class="dash-mode">{{ (l.mode === 'Online' ? 'dashHub.Online' : 'dashHub.InPerson') | translate }}</span>
        <i class="bi" [class.bi-camera-video]="l.mode === 'Online'" [class.bi-geo-alt]="l.mode !== 'Online'"></i>
      </div>
    </div>
  </div>
</ng-template>
